<template>
	<vs-popup scrollable title="VVB Bestellingen Verwerken (beta)" :active.sync="active" class="vvb-modal-popup">
		<vs-alert v-if="vvbOrders.length > 50" class="mb-2 pt-2 pb-2 h-auto"  color="warning">
			<span slot="title">Opmerking</span>
			<span>
					U hebt meer dan 50 VVB bestellingen geselecteerd.
					Slechts de eerste 50 worden weergegeven en verwerkt.
				</span>
		</vs-alert>
		<div class="orders-list mb-4">
			<div v-for="order in displayedVvbOrders" :key="order.order.id" class="vvb-order-item mb-3 p-3 border rounded">
				<div class="flex justify-between items-start mb-2">
					<div>
						<h5 class="font-bold">Bestelling {{ order.order.type_id_visible }}</h5>
					</div>
				</div>

				<div class="products-list mb-3">
					<h6 class="text-sm font-semibold mb-2">Producten:</h6>
					<div v-for="(item, index) in order.items" :key="index" class="text-sm ml-2">
						- {{ item.title }} ({{ item.quantity }}x)
					</div>
				</div>

				<div class="delivery-option">
					<label class="text-sm font-semibold">VVB Label type:</label>
					<select v-model="selectedDeliveryOptions[order.order.id]" class="vs-input-block mt-1 w-full">
						<option value="">Selecteer label...</option>
						<option v-for="option in order.delivery_options" :key="option.id" :value="option.id">
							{{ option.label_display_name }}
						</option>
					</select>
				</div>
			</div>
		</div>

		<div class="flex gap-2 justify-end">
			<vs-button color="danger" @click="onClose">
				Annuleren
			</vs-button>

			<vs-button
				color="primary"
				@click="downloadLabels"
				:disabled="!canDownload || isDownloading"
			>
					<span v-if="isDownloading">
						<i class="fas fa-spinner fa-spin mr-2"></i>
						Downloaden...
					</span>
				<span v-else>
						Download Labels
					</span>
			</vs-button>

			<vs-button
				color="success"
				@click="processOrders"
				:disabled="!canProcess || isProcessing"
			>
					<span v-if="isProcessing">
						<i class="fas fa-spinner fa-spin mr-2"></i>
						Verwerken...
					</span>
				<span v-else>
						Verwerken
					</span>
			</vs-button>
		</div>
	</vs-popup>
</template>

<script>
import apiClient from '@/services/apiClient';
import downloadClient from '@/services/downloadClient';

export default {
	name: 'vvbprocessmodal',
	data() {
		return {
			active: false,
			vvbOrders: [],
			selectedDeliveryOptions: {},
			labelsDownloaded: false,
			isDownloading: false,
			isProcessing: false,
		}
	},
	computed: {
		displayedVvbOrders() {
			return this.vvbOrders.slice(0, 50);
		},
		canDownload() {
			if (this.displayedVvbOrders.length === 0) {
				return false;
			}
			return this.displayedVvbOrders.every(order => this.selectedDeliveryOptions[order.order.id]);
		},
		canProcess() {
			return this.labelsDownloaded && !this.isDownloading;
		}
	},
	watch: {
		active(newVal) {
			if (!newVal) {
				this.reset();
			}
		}
	},
	methods: {
		openModal(orders) {
			this.vvbOrders = orders;
			this.selectedDeliveryOptions = {};
			this.labelsDownloaded = false;
			this.isDownloading = false;
			this.isProcessing = false;

			this.displayedVvbOrders.forEach(order => {
				const recommendedOption = order.delivery_options && order.delivery_options.find(opt => opt.recommended);
				this.selectedDeliveryOptions[order.order.id] = recommendedOption ? recommendedOption.id : '';
			});

			this.active = true;
		},
		reset() {
			this.vvbOrders = [];
			this.selectedDeliveryOptions = {};
			this.labelsDownloaded = false;
			this.isDownloading = false;
			this.isProcessing = false;
		},
		async downloadLabels() {
			if (!this.canDownload) return;

			this.isDownloading = true;

			try {
				const orderIds = this.displayedVvbOrders.map(o => o.order.id);
				const deliveryOptionIds = orderIds.map(id => this.selectedDeliveryOptions[id]);

				await apiClient.put('orders/ship/vvb/select', {
					order_ids: orderIds,
					delivery_options: deliveryOptionIds
				});

				await downloadClient.downloadVvbBulkLabelsPdf(orderIds);

				this.labelsDownloaded = true;

				this.$vs.notify({
					title: 'Succes',
					text: 'Labels succesvol gedownload',
					color: 'success'
				});
			} catch (error) {
				console.error('Error downloading labels:', error);
				this.$vs.notify({
					title: 'Fout',
					text: 'Het downloaden van labels is mislukt',
					color: 'danger'
				});
			} finally {
				this.isDownloading = false;
			}
		},
		async processOrders() {
			if (!this.canProcess) return;

			this.isProcessing = true;

			try {
				const orderIds = this.displayedVvbOrders.map(o => o.order.id);

				const response = await apiClient.post('orders/ship/vvb/ship', {
					order_ids: orderIds
				});

				const results = response.data;
				const failedOrders = Object.entries(results).filter(([_, result]) => !result.success);

				if (failedOrders.length > 0) {
					this.$vs.notify({
						title: 'Gedeeltelijk voltooid',
						text: `${failedOrders.length} bestelling(en) kon(den) niet verwerkt worden`,
						color: 'warning'
					});
				} else {
					this.$vs.notify({
						title: 'Succes',
						text: 'Alle bestellingen succesvol verwerkt',
						color: 'success'
					});
				}

				this.$emit('orders-processed', results);
				this.onClose();
			} catch (error) {
				console.error('Error processing orders:', error);
				this.$vs.notify({
					title: 'Fout',
					text: 'Het verwerken van bestellingen is mislukt',
					color: 'danger'
				});
			} finally {
				this.isProcessing = false;
			}
		},
		onClose() {
			this.active = false;
		}
	}
}
</script>

<style scoped>
.vvb-order-item {
	background-color: #f5f5f5;
	border: 1px solid #e0e0e0;
	color: #333;
}

body.theme-dark .vvb-order-item,
body.theme-semi-dark .vvb-order-item {
	background-color: rgba(255, 255, 255, 0.05) !important;
	border: 1px solid rgba(255, 255, 255, 0.1) !important;
	color: #e0e0e0 !important;
}

.products-list {
	background-color: white;
	padding: 8px;
	border-radius: 4px;
	color: #333;
}

body.theme-dark .products-list,
body.theme-semi-dark .products-list {
	background-color: rgba(255, 255, 255, 0.05) !important;
	color: #e0e0e0 !important;
}

.delivery-option select {
	padding: 8px;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 14px;
	width: 100%;
}

.flex {
	display: flex;
}

.gap-2 {
	gap: 0.5rem;
}

.justify-end {
	justify-content: flex-end;
}

.mb-4 {
	margin-bottom: 1rem;
}

.mb-3 {
	margin-bottom: 0.75rem;
}

.mb-2 {
	margin-bottom: 0.5rem;
}

.mt-1 {
	margin-top: 0.25rem;
}

.p-3 {
	padding: 0.75rem;
}

.w-full {
	width: 100%;
}

.text-sm {
	font-size: 0.875rem;
}

.font-bold {
	font-weight: bold;
}

.font-semibold {
	font-weight: 600;
	color: inherit;
}

body.theme-dark .font-semibold {
	color: #ccc;
}

.rounded {
	border-radius: 0.25rem;
}

.text-gray-600 {
	color: #4b5563;
}

.ml-2 {
	margin-left: 0.5rem;
}

.items-start {
	align-items: flex-start;
}

.items-center {
	align-items: center;
}

.justify-between {
	justify-content: space-between;
}

.border {
	border: 1px solid #ddd;
}
</style>
